import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { cn } from "../utils/style";
function PaginationFull({ paginator, get, set }) {
  const { t } = useTranslation();
  const lastPage = paginator?.lastPage || 1;
  const currentPage = paginator?.currentPage || 1;
  if (currentPage === 1 && lastPage === 1) {
    return <div className="mt-3"></div>;
  }
  const handlePrev = () => {
    if (currentPage > 1) set(currentPage - 1);
  };
  const handleNext = () => {
    if (currentPage < lastPage) set(currentPage + 1);
  };
  return (
    <nav className="flex flex-col sm:flex-row items-center justify-between gap-6 w-full mt-10 px-1">
      {/* Page Status - Mobile: Center, Desktop: Left */}
      <div className="order-2 sm:order-1">
        <p className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
          <span className="h-px w-4 bg-slate-200 hidden sm:block"></span>
          {t("Page")}
          <span className="text-primary-600 bg-primary-50 px-2 py-0.5 rounded-md border border-primary-100/50 min-w-[24px] text-center">
            {currentPage}
          </span>
          <span className="text-slate-300">/</span>
          <span className="text-slate-600">{lastPage}</span>
        </p>
      </div>

      {/* Pagination Controls */}
      <div className="order-1 sm:order-2 w-full sm:w-auto">
        <div className="flex items-center justify-center sm:justify-end gap-2">
          {/* Previous Button */}
          <button
            onClick={handlePrev}
            disabled={currentPage === 1}
            className={cn(
              "relative inline-flex items-center justify-center rounded-xl p-2.5 transition-all duration-300 border shadow-sm",
              currentPage === 1
                ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                : "bg-white text-slate-600 border-slate-200 hover:border-primary-500 hover:text-primary-600 hover:shadow-md hover:shadow-primary-500/5 active:scale-90",
            )}
          >
            <span className="sr-only">{t("Previous")}</span>
            <ChevronLeftIcon aria-hidden="true" className="h-5 w-5" />
          </button>

          {/* Page Numbers - Hidden on extra small mobile, limited on small mobile */}
          <div className="hidden xs:flex items-center gap-1.5 mx-1">
            {getPageNumbers(lastPage, currentPage).map((number, index) => (
              <React.Fragment key={index}>
                {number === null ? (
                  <PaginationGap />
                ) : (
                  <PaginationItem
                    number={number}
                    active={currentPage === number}
                    set={set}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Mobile Only Page Indicator (Alternative to numbers list) */}
          <div className="xs:hidden flex items-center px-4 font-bold text-sm text-slate-700">
            {currentPage}
            {t("of")}
            {lastPage}
          </div>

          {/* Next Button */}
          <button
            onClick={handleNext}
            disabled={currentPage === lastPage}
            className={cn(
              "relative inline-flex items-center justify-center rounded-xl p-2.5 transition-all duration-300 border shadow-sm",
              currentPage === lastPage
                ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                : "bg-white text-slate-600 border-slate-200 hover:border-primary-500 hover:text-primary-600 hover:shadow-md hover:shadow-primary-500/5 active:scale-90",
            )}
          >
            <span className="sr-only">{t("Next")}</span>
            <ChevronRightIcon aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
      </div>
    </nav>
  );
}
function PaginationGap() {
  return (
    <span className="w-8 flex justify-center text-slate-300 font-black tracking-widest text-xs">
      •••
    </span>
  );
}
function PaginationItem({ number, active, set }) {
  return (
    <button
      onClick={() => set(number)}
      disabled={active}
      className={cn(
        "relative flex items-center justify-center w-10 h-10 rounded-xl text-sm font-bold transition-all duration-300 border shadow-sm",
        active
          ? "bg-primary-600 text-white border-primary-500 shadow-lg shadow-primary-500/20 scale-105 z-10"
          : "bg-white text-slate-600 border-slate-200 hover:border-primary-300 hover:text-primary-600 hover:shadow-md active:scale-95",
      )}
    >
      {number}
    </button>
  );
}
function getPageNumbers(totalPages, currentPage) {
  let pageNumbers = [];

  // Simple strategy for better responsiveness:
  // Show fewer neighbors on smaller screens if we could detect it,
  // but here we use a balanced set that works well with 'hidden' classes.
  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
  } else {
    if (currentPage <= 3) {
      pageNumbers = [1, 2, 3, 4, null, totalPages];
    } else if (currentPage >= totalPages - 2) {
      pageNumbers = [
        1,
        null,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    } else {
      pageNumbers = [
        1,
        null,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        null,
        totalPages,
      ];
    }
  }
  return pageNumbers;
}
export default PaginationFull;
